/**
 * AI Model Configuration
 *
 * Centralized definitions for the AI models the app routes to via the backend
 * Lambdas. The user-facing UI does NOT expose a picker — newsletter generation
 * always uses DEFAULT_MODEL. The multi-model surface here is retained so
 * NewsletterAIService can fall back to a cheaper model on rate-limit / cost
 * pressure without code changes, and so an A/B can pin a specific model.
 *
 * Providers:
 *   - OPENAI:  Direct OpenAI API (frontend-pinned key)
 *   - BEDROCK: Claude in Amazon Bedrock (preferred — same account, audit
 *              trail, no extra egress)
 *
 * Bedrock model IDs (Claude in Amazon Bedrock, Messages-API endpoint,
 * migrated 2026-09):
 *   The backend Lambdas call `bedrock-mantle.<region>.api.aws/anthropic/v1/messages`
 *   through the Anthropic SDK. On that endpoint model IDs carry the bare
 *   `anthropic.` provider prefix — no `us.`/`global.` inference-profile
 *   prefix and no `-vN:0` suffix. The Lambda validates `modelId` against
 *   the same list (ALLOWED_BEDROCK_MODELS in
 *   amplify/functions/newsletter-ai-api/handler.ts); anything else falls
 *   back to the Lambda default, so keep the two lists in sync.
 *
 *   Roles: Claude Sonnet 5 is the default for every AI action (polish,
 *   rewrites, subject lines, replies — short, format-bound work). Claude
 *   Opus 5 stays allowlisted for the Apply My Style A/B; it is gated per
 *   account in the Bedrock console. Claude Haiku 4.5 is the economy fallback.
 */

export const AI_PROVIDERS = {
  OPENAI: 'openai',
  BEDROCK: 'bedrock'
};

export const AI_MODELS = {
  'claude-sonnet-5': {
    id: 'anthropic.claude-sonnet-5',
    name: 'Claude Sonnet 5',
    provider: AI_PROVIDERS.BEDROCK,
    description: 'Default — balanced quality, cost and latency for polish, rewrites and subject lines',
    costTier: 'standard',
    maxTokens: 1000000
  },
  'claude-opus-5': {
    id: 'anthropic.claude-opus-5',
    name: 'Claude Opus 5',
    provider: AI_PROVIDERS.BEDROCK,
    description: 'Allowlisted for the Apply My Style A/B — strongest drafting quality, ~2.5x the cost',
    costTier: 'premium',
    maxTokens: 1000000
  },
  'claude-haiku-4-5': {
    id: 'anthropic.claude-haiku-4-5',
    name: 'Claude Haiku 4.5',
    provider: AI_PROVIDERS.BEDROCK,
    description: 'Fallback for high-volume / cost-sensitive runs',
    costTier: 'economy',
    maxTokens: 200000
  },
  'gpt-4o': {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: AI_PROVIDERS.OPENAI,
    description: 'OpenAI fallback for direct-API code paths',
    costTier: 'premium',
    maxTokens: 128000
  }
};

// Single source of truth — the wizard does NOT let users pick. If you want
// to A/B a different default, change THIS line and redeploy.
export const DEFAULT_MODEL = 'claude-sonnet-5';

/**
 * Returns all models for a given provider, in catalog order (the first
 * entry is the provider's default — the failover path relies on this).
 * @param {string} provider - One of AI_PROVIDERS values
 * @returns {Array} Array of model config objects
 */
export function getModelsByProvider(provider) {
  return Object.values(AI_MODELS).filter(m => m.provider === provider);
}

/**
 * Returns model config by key (e.g. 'claude-sonnet-5').
 * Falls back to DEFAULT_MODEL if the key is not found.
 * @param {string} modelId - Model key from AI_MODELS
 * @returns {Object} Model config object
 */
export function getModelById(modelId) {
  return AI_MODELS[modelId] || AI_MODELS[DEFAULT_MODEL];
}
